import React, { useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { fetchPieces } from "../services/api";
import Spinner from "./Spinner";

const CompEntriesbyCountryChart = ({ startDate, promptId }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAndTransformData = async () => {
      setIsLoading(true);

      try {
        const rawData = await fetchPieces(startDate, promptId);
 
        const filteredEntries = rawData.filter(entry =>
          entry.submitted === true &&
          entry.raw_submitted_at && 
          entry.raw_submitted_at !== "NaT" &&
          entry.raw_submitted_at !== "None"
        );

        // Count by country
        const countryCounts = filteredEntries.reduce((acc, entry) => {
          const country = entry.country || "Unknown";
          acc[country] = (acc[country] || 0) + 1;
          return acc;
        }, {});

        // Format for Nivo
        const allCountries = Object.entries(countryCounts)
          .map(([country, count]) => ({
            id: country,
            label: country,
            value: count
          }))
          .filter(d => typeof d.value === "number" && !isNaN(d.value))
          
        // Sort descending by count
        allCountries.sort((a, b) => b.value - a.value);

        // Split top 9 and others
        const top9 = allCountries.slice(0, 9);
        const others = allCountries.slice(9);

        // Sum the "others"
        const othersTotal = others.reduce((sum, d) => sum + d.value, 0);

        // Final data with "Others" appended
        const formattedData = [
          ...top9,
          ...(othersTotal > 0
            ? [{ id: "Others", label: "Others", value: othersTotal }]
            : [])
        ];
        setData(formattedData);
        console.log("Has duplicates?", new Set(formattedData.map(d => d.id)).size !== formattedData.length);

        
      } catch (error) {
        console.error("Error loading competition entries:", error);
      } finally {
        setIsLoading(false);
      }
      
    };

    // reset data immediately to trigger spinner on prop change
    setData([]);
    fetchAndTransformData();
  }, [startDate, promptId]);

  const handleSliceClick = (datum) => {
    console.log("Slice clicked:", datum); 
  };
  const isValidData = Array.isArray(data)
  && data.length > 0
  && data.every(d =>
      typeof d.id === "string" &&
      typeof d.label === "string" &&
      typeof d.value === "number" &&
      !isNaN(d.value)
  );
  console.log("Is valid pie data?", isValidData);
  if (!isValidData) console.log("Invalid pie data preview:", data);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      {isLoading ? (
        <Spinner label="Loading chart…" />
      ) : isValidData ? (
        <div style={{ height: "400px", width: "700px" }}> {/* Set width as needed */}
          <ResponsivePie
            data={data}
            margin={{ top: 40, right: 160, bottom: 40, left: 40 }}
            innerRadius={0.4}
            padAngle={1}
            cornerRadius={3}
            colors={{ scheme: "category10" }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            // Show percentage labels on slices
            arcLabel={(d) => {
              const total = data.reduce((sum, item) => sum + item.value, 0);
              const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0";
              return `${pct}%`;
            }}
            // Custom tooltip: Country: count (percent)
            tooltip={({ datum }) => {
              const total = data.reduce((sum, item) => sum + item.value, 0);
              const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : "0.0";
              return (
                <div style={{
                  background: "white",
                  padding: "6px 9px",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  color: "#333"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 12, height: 12, background: datum.color, display: "inline-block", borderRadius: 2 }} />
                    <span style={{ fontWeight: 600 }}>{datum.id}</span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span style={{ fontWeight: 600 }}>{datum.value}</span>
                    <span>{` (${pct}%)`}</span>
                  </div>
                </div>
              );
            }}
            legends={[
              {
                anchor: "right",
                direction: "column",
                translateX: 120,
                itemWidth: 80,
                itemHeight: 20,
                itemsSpacing: 4,
                symbolSize: 12,
                symbolShape: "circle"
              }
            ]}
            onClick={handleSliceClick}
          />
        </div>
      ) : (
        <p>No data available.</p>
      )}
    </div>
  );
  
  
};

export default CompEntriesbyCountryChart;
